import React from 'react';
import { NavLink, Outlet, Navigate, Link } from 'react-router-dom';
import {
  LayoutGrid, Image, Sparkles, ListChecks, Star, Users,
  HelpCircle, MessageSquare, Mail, Settings, Search, LogOut, ExternalLink,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';

const NAV_GROUPS = [
  {
    title: 'General',
    items: [
      { to: '/admin',          label: 'Dashboard',  icon: LayoutGrid, end: true },
      { to: '/admin/leads',    label: 'Leads',      icon: Mail },
    ],
  },
  {
    title: 'Biblioteca',
    items: [
      { to: '/admin/media',                 label: 'Media Library',  icon: Image },
    ],
  },
  {
    title: 'Contenido',
    items: [
      { to: '/admin/content/site',                label: 'Sitio',           icon: Settings },
      { to: '/admin/content/seo',                 label: 'SEO',             icon: Search },
      { to: '/admin/content/intro_hero',          label: 'Intro / Hero',    icon: Sparkles },
      { to: '/admin/content/manifesto',           label: 'Manifiesto',      icon: ListChecks },
      { to: '/admin/content/showcase',            label: 'Showcase',        icon: Image },
      { to: '/admin/content/features',            label: 'Plataforma',      icon: ListChecks },
      { to: '/admin/content/how',                 label: 'Pipeline',        icon: ListChecks },
      { to: '/admin/content/comparison',          label: 'vs IA genérica',  icon: ListChecks },
      { to: '/admin/content/compliance_shield',   label: 'Compliance / Regulador', icon: ListChecks },
      { to: '/admin/content/likeness_vault',      label: 'Likeness Vault',  icon: Users },
      { to: '/admin/content/impact_esg',          label: 'Impact / ESG',    icon: Sparkles },
      { to: '/admin/content/metrics_strip',       label: 'Métricas',        icon: ListChecks },
      { to: '/admin/content/looks_hall_of_fame',  label: 'Looks Hall',      icon: Star },
      { to: '/admin/content/capabilities',        label: 'Capacidades IA',  icon: Sparkles },
      { to: '/admin/content/pricing',             label: 'Precios',         icon: Wallet },
      { to: '/admin/content/testimonials',        label: 'Testimonios',     icon: Star },
      { to: '/admin/content/logos',               label: 'Logos',           icon: Image },
      { to: '/admin/content/team',                label: 'Equipo',          icon: Users },
      { to: '/admin/content/faq',                 label: 'FAQ',             icon: HelpCircle },
      { to: '/admin/content/contact',             label: 'Contacto',        icon: MessageSquare },
      { to: '/admin/content/footer',              label: 'Footer',          icon: Settings },
    ],
  },
  {
    title: 'Configuración',
    items: [
      { to: '/admin/settings/smtp',    label: 'SMTP',     icon: Mail },
      { to: '/admin/settings/general', label: 'General',  icon: Settings },
    ],
  },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-bg text-ink-muted">Cargando…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-bg text-ink flex" data-testid="admin-layout">
      <aside className="w-64 flex-shrink-0 bg-bg-secondary border-r border-line flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-line">
          <Link to="/" className="font-display text-2xl text-ink hover:text-acid transition-colors flex items-center gap-2">
            MODARIA <span className="text-eyebrow text-acid">/admin</span>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {NAV_GROUPS.map((g) => (
            <div key={g.title} className="mb-6">
              <div className="text-eyebrow text-ink-dim px-3 mb-2">{g.title}</div>
              {g.items.map((it) => {
                const Ic = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`admin-nav-${it.to.split('/').pop() || 'dashboard'}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-mono transition-colors ${
                        isActive ? 'bg-acid/10 text-acid border-l-2 border-acid' : 'text-ink-muted hover:text-ink hover:bg-bg-surface'
                      }`
                    }
                  >
                    <Ic size={15} /> {it.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-line space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] text-ink-muted hover:text-acid"
            data-testid="admin-view-site"
          >
            <ExternalLink size={12} /> Ver sitio
          </a>
          <div className="px-3 py-2 text-xs text-ink-muted truncate">{user.email}</div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] text-ink-muted hover:text-danger"
            data-testid="admin-logout"
          >
            <LogOut size={12} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" data-testid="admin-main">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
